import React from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Crown, Boxes, Layers } from "lucide-react";

/* ------------------------------------------------------------------
   Visual form editor for the "struktur_organisasi" document.
   The public page (StrukturOrganisasi.jsx) renders a fixed 3-tier tree:
     Root (Karumkit)
       └─ Sections (root.children)  ── grid
            └─ per section either:
                 (a) leaf units          -> children = [leaf, ...]
                 (b) sub-groups w/ leaves -> children = [{...,children:[leaf]}]
   Colors are chosen from presets so admins never touch Tailwind classes.
   Node cards use gradient tints (from-… to-…); leaf badges use
   bg-…/border-…/text-… classes.
   ------------------------------------------------------------------ */

const uid = (p = "node") => `${p}-${Math.random().toString(36).slice(2, 8)}`;

const COLORS = [
  { key: "blue", label: "Biru — Pimpinan", grad: "from-blue-700 to-blue-900", leaf: "bg-blue-50 border-blue-200 text-blue-800", sw: "from-blue-700 to-blue-900" },
  { key: "emerald", label: "Hijau — Pelayanan Medis", grad: "from-emerald-500 to-emerald-700", leaf: "bg-emerald-50 border-emerald-200 text-emerald-800", sw: "from-emerald-500 to-emerald-700" },
  { key: "amber", label: "Kuning — Penunjang", grad: "from-amber-500 to-amber-700", leaf: "bg-amber-50 border-amber-200 text-amber-800", sw: "from-amber-500 to-amber-700" },
  { key: "red", label: "Merah — Pengawasan", grad: "from-red-500 to-red-700", leaf: "bg-red-50 border-red-200 text-red-800", sw: "from-red-500 to-red-700" },
  { key: "slate", label: "Abu — Administrasi", grad: "from-slate-600 to-slate-800", leaf: "bg-slate-50 border-slate-200 text-slate-800", sw: "from-slate-600 to-slate-800" },
  { key: "purple", label: "Ungu — Pembinaan", grad: "from-purple-600 to-purple-800", leaf: "bg-purple-50 border-purple-200 text-purple-800", sw: "from-purple-600 to-purple-800" },
  { key: "teal", label: "Toska", grad: "from-teal-500 to-teal-700", leaf: "bg-teal-50 border-teal-200 text-teal-800", sw: "from-teal-500 to-teal-700" },
];

const NODE_ICONS = ["", "Stethoscope", "FlaskConical", "ShieldCheck", "Users", "Network"];

const colorKeyFromTint = (tint) => {
  const s = String(tint || "");
  const found = COLORS.find((c) => s.includes(c.key));
  return found ? found.key : "blue";
};
const gradByKey = (k) => (COLORS.find((c) => c.key === k) || COLORS[0]).grad;
const leafByKey = (k) => (COLORS.find((c) => c.key === k) || COLORS[0]).leaf;

const inputCls =
  "w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 bg-white";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>
    {children}
  </label>
);

const Text = ({ value, onChange, placeholder, testid }) => (
  <input
    type="text"
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    data-testid={testid}
    className={inputCls}
  />
);

const ColorSelect = ({ value, onChange, testid }) => {
  const key = colorKeyFromTint(value);
  return (
    <div className="flex items-center gap-2">
      <span className={`h-8 w-8 shrink-0 rounded-lg bg-gradient-to-br ${gradByKey(key)} shadow-inner border border-white/40`} />
      <select
        value={key}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className={inputCls}
      >
        {COLORS.map((c) => (
          <option key={c.key} value={c.key}>{c.label}</option>
        ))}
      </select>
    </div>
  );
};

const IconSelect = ({ value, onChange }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
    {NODE_ICONS.map((ic) => (
      <option key={ic || "none"} value={ic}>{ic || "— Tanpa ikon —"}</option>
    ))}
  </select>
);

const arrayOps = (arr, onChange) => {
  const list = Array.isArray(arr) ? arr : [];
  return {
    list,
    set: (i, v) => onChange(list.map((x, idx) => (idx === i ? v : x))),
    add: (v) => onChange([...list, v]),
    remove: (i) => onChange(list.filter((_, idx) => idx !== i)),
    move: (i, dir) => {
      const j = i + dir;
      if (j < 0 || j >= list.length) return;
      const copy = list.slice();
      const [it] = copy.splice(i, 1);
      copy.splice(j, 0, it);
      onChange(copy);
    },
  };
};

const RowControls = ({ index, count, onMove, onRemove }) => (
  <div className="flex items-center gap-1">
    <button type="button" onClick={() => onMove(-1)} disabled={index === 0} title="Naikkan" className="h-7 w-7 rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center">
      <ArrowUp className="h-3.5 w-3.5" />
    </button>
    <button type="button" onClick={() => onMove(1)} disabled={index === count - 1} title="Turunkan" className="h-7 w-7 rounded-lg text-slate-500 hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center">
      <ArrowDown className="h-3.5 w-3.5" />
    </button>
    <button type="button" onClick={onRemove} title="Hapus" className="h-7 w-7 rounded-lg text-red-500 hover:bg-red-50 flex items-center justify-center">
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  </div>
);

const AddButton = ({ onClick, label, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-blue-800 hover:bg-blue-900 text-white"
  >
    <Plus className="h-3.5 w-3.5" />
    {label}
  </button>
);

// ---- Leaf editor (unit) ----
const LeafEditor = ({ leaf, index, count, onChange, onRemove, onMove, testid }) => (
  <div data-testid={testid} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-semibold text-slate-400">Unit #{index + 1}</span>
      <RowControls index={index} count={count} onMove={onMove} onRemove={onRemove} />
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <Field label="Nama Singkat (short)">
        <Text value={leaf.short} onChange={(v) => onChange({ ...leaf, short: v })} placeholder="IGD 24 Jam" />
      </Field>
      <Field label="Nama Lengkap (title)">
        <Text value={leaf.title} onChange={(v) => onChange({ ...leaf, title: v })} placeholder="Instalasi Gawat Darurat" />
      </Field>
    </div>
    <Field label="Warna">
      <ColorSelect value={leaf.tint} onChange={(k) => onChange({ ...leaf, tint: leafByKey(k) })} />
    </Field>
  </div>
);

const LeafList = ({ items, onChange, addLabel, testid }) => {
  const ops = arrayOps(items, onChange);
  return (
    <div className="space-y-2" data-testid={testid}>
      {ops.list.map((leaf, i) => (
        <LeafEditor
          key={leaf.id || i}
          testid={`${testid}-leaf-${i}`}
          leaf={leaf}
          index={i}
          count={ops.list.length}
          onChange={(v) => ops.set(i, v)}
          onRemove={() => ops.remove(i)}
          onMove={(d) => ops.move(i, d)}
        />
      ))}
      {ops.list.length === 0 && <div className="text-xs text-slate-400">Belum ada unit.</div>}
      <AddButton
        testid={`${testid}-add`}
        label={addLabel}
        onClick={() => ops.add({ id: uid("u"), title: "", short: "", tint: leafByKey("emerald") })}
      />
    </div>
  );
};

// ---- Subgroup editor (a mid node that owns leaves) ----
const SubgroupEditor = ({ node, index, count, onChange, onRemove, onMove }) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 space-y-3" data-testid={`struktur-subgroup-${index}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
        <Layers className="h-4 w-4 text-slate-400" />
        Sub-bagian #{index + 1}
      </div>
      <RowControls index={index} count={count} onMove={onMove} onRemove={onRemove} />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
      <Field label="Jabatan (title)">
        <Text value={node.title} onChange={(v) => onChange({ ...node, title: v })} placeholder="Pelayanan Medis & Perawatan" />
      </Field>
      <Field label="Sebutan (short)">
        <Text value={node.short} onChange={(v) => onChange({ ...node, short: v })} placeholder="Kaur Yanmedwat" />
      </Field>
      <Field label="Nama Pejabat">
        <Text value={node.name} onChange={(v) => onChange({ ...node, name: v })} placeholder="dr. [Nama], Sp.PK" />
      </Field>
      <Field label="Pangkat">
        <Text value={node.rank} onChange={(v) => onChange({ ...node, rank: v })} placeholder="AKP" />
      </Field>
      <Field label="Ikon">
        <IconSelect value={node.icon} onChange={(v) => onChange({ ...node, icon: v })} />
      </Field>
      <Field label="Warna">
        <ColorSelect value={node.tint} onChange={(k) => onChange({ ...node, tint: gradByKey(k) })} />
      </Field>
    </div>
    <div>
      <div className="text-xs font-medium text-slate-600 mb-1">Unit di bawahnya</div>
      <LeafList
        items={node.children}
        onChange={(v) => onChange({ ...node, children: v })}
        addLabel="Tambah Unit"
        testid={`struktur-subgroup-${index}-leaves`}
      />
    </div>
  </div>
);

// ---- Section editor (root.children) ----
const SectionEditor = ({ section, index, count, onChange, onRemove, onMove }) => {
  const children = Array.isArray(section.children) ? section.children : [];
  const isSub = children.length > 0 && Array.isArray(children[0]?.children);

  const setMode = (sub) => {
    if (sub) {
      if (isSub) return;
      const leaves = children;
      onChange({
        ...section,
        children: leaves.length
          ? [{ id: uid("sub"), title: "Sub-bagian", short: "Sub", name: "", rank: "", tint: gradByKey("emerald"), children: leaves }]
          : [],
      });
    } else {
      if (!isSub) return;
      const leaves = children.flatMap((sg) => (Array.isArray(sg.children) ? sg.children : []));
      onChange({ ...section, children: leaves });
    }
  };

  const subOps = arrayOps(children, (v) => onChange({ ...section, children: v }));

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-4 md:p-5 space-y-3" data-testid={`struktur-section-${index}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <Boxes className="h-4 w-4 text-blue-800" />
          Bagian #{index + 1}
        </div>
        <RowControls index={index} count={count} onMove={onMove} onRemove={onRemove} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Field label="Jabatan (title)">
          <Text value={section.title} onChange={(v) => onChange({ ...section, title: v })} placeholder="Perencanaan & Administrasi" />
        </Field>
        <Field label="Sebutan (short)">
          <Text value={section.short} onChange={(v) => onChange({ ...section, short: v })} placeholder="Kabag Renmin" />
        </Field>
        <Field label="Nama Pejabat">
          <Text value={section.name} onChange={(v) => onChange({ ...section, name: v })} placeholder="[Nama], S.E" />
        </Field>
        <Field label="Pangkat">
          <Text value={section.rank} onChange={(v) => onChange({ ...section, rank: v })} placeholder="KOMPOL" />
        </Field>
        <Field label="Ikon">
          <IconSelect value={section.icon} onChange={(v) => onChange({ ...section, icon: v })} />
        </Field>
        <Field label="Warna">
          <ColorSelect value={section.tint} onChange={(k) => onChange({ ...section, tint: gradByKey(k) })} />
        </Field>
      </div>

      {/* Mode toggle */}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <span className="text-xs text-slate-500">Isi bagian ini sebagai:</span>
        <div className="inline-flex rounded-full bg-slate-100 p-0.5" data-testid={`struktur-section-${index}-mode`}>
          <button
            type="button"
            onClick={() => setMode(false)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition ${!isSub ? "bg-white shadow text-blue-800" : "text-slate-500"}`}
          >
            Daftar Unit
          </button>
          <button
            type="button"
            onClick={() => setMode(true)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition ${isSub ? "bg-white shadow text-blue-800" : "text-slate-500"}`}
          >
            Sub-bagian
          </button>
        </div>
      </div>

      {/* Children */}
      {isSub ? (
        <div className="space-y-3">
          {subOps.list.map((sg, i) => (
            <SubgroupEditor
              key={sg.id || i}
              node={sg}
              index={i}
              count={subOps.list.length}
              onChange={(v) => subOps.set(i, v)}
              onRemove={() => subOps.remove(i)}
              onMove={(d) => subOps.move(i, d)}
            />
          ))}
          <AddButton
            testid={`struktur-section-${index}-add-subgroup`}
            label="Tambah Sub-bagian"
            onClick={() => subOps.add({ id: uid("sub"), title: "", short: "", name: "", rank: "", icon: "", tint: gradByKey("emerald"), children: [] })}
          />
        </div>
      ) : (
        <LeafList
          items={children}
          onChange={(v) => onChange({ ...section, children: v })}
          addLabel="Tambah Unit"
          testid={`struktur-section-${index}-leaves`}
        />
      )}
    </div>
  );
};

const StrukturForm = ({ value, onChange }) => {
  const root = value || {};
  const patch = (p) => onChange({ ...root, ...p });
  const sections = arrayOps(root.children, (v) => patch({ children: v }));

  return (
    <div className="space-y-5" data-testid="struktur-form">
      {/* Root */}
      <section className="rounded-3xl border border-blue-100 bg-blue-50/40 shadow-sm p-5 md:p-6 space-y-3" data-testid="struktur-form-root">
        <div className="flex items-center gap-2">
          <div className="h-9 w-9 rounded-xl bg-blue-800 text-white flex items-center justify-center">
            <Crown className="h-5 w-5" />
          </div>
          <div>
            <div className="font-heading font-semibold text-slate-900">Pimpinan Puncak</div>
            <div className="text-xs text-slate-500">Kepala Rumah Sakit (node teratas bagan)</div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Field label="Jabatan (title)">
            <Text value={root.title} onChange={(v) => patch({ title: v })} placeholder="Kepala Rumah Sakit" testid="struktur-root-title" />
          </Field>
          <Field label="Sebutan (short)">
            <Text value={root.short} onChange={(v) => patch({ short: v })} placeholder="Karumkit" />
          </Field>
          <Field label="Nama Pejabat">
            <Text value={root.name} onChange={(v) => patch({ name: v })} placeholder="dr. [Nama], Sp.PD, M.Kes" />
          </Field>
          <Field label="Pangkat">
            <Text value={root.rank} onChange={(v) => patch({ rank: v })} placeholder="AKBP" />
          </Field>
          <Field label="Warna">
            <ColorSelect value={root.tint} onChange={(k) => patch({ tint: gradByKey(k) })} />
          </Field>
        </div>
      </section>

      {/* Sections */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-700">Bagian di bawah Pimpinan</div>
        <AddButton
          testid="struktur-add-section"
          label="Tambah Bagian"
          onClick={() => sections.add({ id: uid("sec"), title: "", short: "", name: "", rank: "", icon: "", tint: gradByKey("slate"), children: [] })}
        />
      </div>
      {sections.list.length === 0 && (
        <div className="text-xs text-slate-400">Belum ada bagian. Klik tombol Tambah Bagian.</div>
      )}
      <div className="space-y-4">
        {sections.list.map((sec, i) => (
          <SectionEditor
            key={sec.id || i}
            section={sec}
            index={i}
            count={sections.list.length}
            onChange={(v) => sections.set(i, v)}
            onRemove={() => sections.remove(i)}
            onMove={(d) => sections.move(i, d)}
          />
        ))}
      </div>

      <p className="text-[11px] text-slate-400">
        Catatan: bagan publik menampilkan 3 tingkat — Pimpinan → Bagian → Unit/Sub-bagian.
        Pilih mode Sub-bagian bila satu bagian memiliki kepala urusan yang membawahi beberapa unit.
      </p>
    </div>
  );
};

export default StrukturForm;
